'use client'

import type { ILogLayer } from '@repo/observability/client-logger'
import type { ErrorReporter } from '@repo/observability/errors'
import type { EventTracker } from '@repo/observability/events'
import { useSessionId } from '@repo/observability/session-id-provider'
import { type PropsWithChildren, useMemo } from 'react'

import type { ActionFlowGasPolicy, ActionFlowStepSettlement } from './types.action-flow'
import { ActionFlowProvider } from './ActionFlowProvider'

export type Props = PropsWithChildren<{
  errorReporter?: ErrorReporter
  eventTracker?: EventTracker
  logger?: ILogLayer
  onComplete?: () => void | Promise<void>
  onStepSettled?: (settlement: ActionFlowStepSettlement) => void
  interTransactionDelayMs?: number
  gasPolicy?: ActionFlowGasPolicy
  trackingContext?: Record<string, unknown>
}>

/**
 * Wires observability into `<ActionFlowProvider>` and injects the
 * server-minted session id into the tracking context. App-specific
 * tracking-context fields should be passed via the `trackingContext` prop.
 */
export function ObservableActionFlowProvider({
  errorReporter,
  eventTracker,
  logger,
  onComplete,
  onStepSettled,
  interTransactionDelayMs,
  gasPolicy,
  trackingContext,
  children
}: Props) {
  const sessionId = useSessionId()

  const mergedTrackingContext = useMemo(
    () => ({
      ...(sessionId && { sessionId }),
      ...trackingContext
    }),
    [sessionId, trackingContext]
  )

  return (
    <ActionFlowProvider
      errorReporter={errorReporter}
      eventTracker={eventTracker}
      logger={logger}
      onComplete={onComplete}
      onStepSettled={onStepSettled}
      interTransactionDelayMs={interTransactionDelayMs}
      gasPolicy={gasPolicy}
      trackingContext={mergedTrackingContext}
    >
      {children}
    </ActionFlowProvider>
  )
}
